/**
 * Geographic utility functions.
 *
 * Consumers:
 *   haversineKm      — poiService (city→POI straight-line distance, POI proximity scoring)
 *   railDistanceKm   — TripStats (display distance + travel-time estimates)
 *                      pricingService (rail cost estimation)
 *   extractCityName  — routeService, hafasService, cityStore (strip station suffixes → clean city name)
 *   distToRouteKm    — (currently unused, kept for future use)
 *   GeoCoord         — (currently unused, kept for future use)
 */

/**
 * Client-side safety net: strip common European station suffixes to extract a
 * clean city-level name.  Mirrors the server-side cleanCityName() in
 * api/_cacheStations.ts but kept lightweight — only the most common patterns.
 *
 * Examples:
 *   "Paris Gare de l'Est"      → "Paris"
 *   "Berlin Hauptbahnhof"      → "Berlin"
 *   "Milano Centrale"          → "Milano"
 *   "Stockholm Centralstation" → "Stockholm"
 *   "Köln Hbf"                 → "Köln"
 *   "Gare du Nord"             → "Nord"  (prefix form)
 */
export function extractCityName(stationName: string): string {
  // Normalize whitespace once so suffix matching never retries long whitespace runs.
  return stationName.replaceAll(/\s+/g, ' ').trim()
    .replace(/^S\+U /i, '')
    .replace(/^[SU] /i, '')
    .replace(/ (Hauptbahnhof|Central ?Station|Centralstation|Centraalstation|Sentralstasjon|hlavní ?nádraží|Centraal|Centrale|Centralna|Centralny)\b.*/i, '')
    .replace(/ (Porta Nuova|Santa Lucia|Piazza Principe|Part-Dieu|Saint-Charles|Matabiau|Joaquín Sorolla|Sants|Atocha|Termini|Keleti|Nyugati|Déli)\b.*/i, '')
    .replace(/ (Hbf|HB|Pbf|Central|Station|Bahnhof|Gare|Stazione|Estación|Estação|Kolodvor|Gl\.|Glavni)\b.*/i, '')
    .replace(/ (Główny|Główna|Wschodnia|Wschodni|Zachodnia|Zachodni|Letisko|Lotnisko)\b.*/i, '')
    .replace(/ (St\.?|Stn\.?)$/i, '')
    .replace(/ [HCS]$/i, '')
    .replace(/^Gare d[eu] (la )?/i, '')
    .replace(/[- ](?:Midi|Nord|Sud|Est|Ouest|Ville|Ost|West)\b.*/i, '')
    .replace(/,.*/, '')
    .trim();
}

/**
 * Haversine formula — great-circle distance between two lat/lng points in km.
 */
export function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Rail-adjusted distance — haversine × 1.3 to approximate track distance.
 * Use this for display distances and travel-time estimates.
 */
export function railDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  return haversineKm(lat1, lon1, lat2, lon2) * 1.3;
}

/** Coordinate pair for route geometry calculations */
export interface GeoCoord {
  lat: number;
  lng: number;
}

/**
 * Minimum distance (km) from a point to a polyline (series of segments).
 * Projects the point onto each segment using flat-earth approximation,
 * then uses haversine for the final distance.
 */
export function distToRouteKm(p: GeoCoord, waypoints: GeoCoord[]): number {
  let min = Infinity;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    let t = dx === 0 && dy === 0
      ? 0
      : ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy);
    t = Math.max(0, Math.min(1, t));
    const d = haversineKm(p.lat, p.lng, a.lat + t * dy, a.lng + t * dx);
    if (d < min) min = d;
  }
  return min;
}
